// src/components/Payment/PaymentResults.js

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Error
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import client from '../../API';

// Success Component
export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const queryClient = useQueryClient();
  const hasFired = React.useRef(false);

  const capturePayPalOrderMutation = useMutation(
    (orderData) => client.balanceLedger.capturePayPalOrder(orderData),
    {
      onSuccess: (data) => {
        setPaymentData(data);
        setProcessing(false);
        queryClient.invalidateQueries(['balance']);
      },
      onError: (error) => {
        if (error.code === 'ERR_CANCELED') return;
        console.error('Payment capture failed:', error);
        setError(error.response?.data?.error || 'Failed to process payment');
        setProcessing(false);
      }
    }
  );

  const handleStripeSuccessMutation = useMutation(
    (sessionData) => client.balanceLedger.handleStripeSuccess(sessionData),
    {
      onSuccess: (data) => {
        setPaymentData(data);
        setProcessing(false);
        queryClient.invalidateQueries(['balance']);
      },
      onError: (error) => {
        if (error.code === 'ERR_CANCELED') return;
        console.error('Stripe success handling failed:', error);
        setError(error.response?.data?.error || 'Failed to process Stripe payment');
        setProcessing(false);
      }
    }
  );

  useEffect(() => {
    if (hasFired.current) return;
    hasFired.current = true;

    const orderId = searchParams.get('order_id') || searchParams.get('token');
    const sessionId = searchParams.get('session_id');
    const provider = searchParams.get('provider');

    if (orderId && provider === 'paypal') {
      capturePayPalOrderMutation.mutate({ order_id: orderId });
    } else if (sessionId && provider === 'stripe') {
      handleStripeSuccessMutation.mutate({ session_id: sessionId });
    } else {
      toast.error('Invalid payment parameters');
      setError('Invalid payment parameters');
      setProcessing(false);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  if (processing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 3 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6">Processing your payment...</Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we confirm your payment
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 3 
      }}>
        <Error sx={{ fontSize: 60, color: 'error.main' }} />
        <Typography variant="h5" color="error.main">
          Payment Processing Failed
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  if (paymentData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 3 
      }}>
        <Paper sx={{ p: 4, maxWidth: 500, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" color="success.main" gutterBottom>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your payment has been processed successfully.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Amount Paid:</Typography>
              <Typography variant="body2">${paymentData.amount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Processing Fee:</Typography>
              <Typography variant="body2">${paymentData.fee}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">Amount Added:</Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                ${paymentData.net_amount}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight="bold">New Balance:</Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                ${paymentData.new_balance}
              </Typography>
            </Box>
          </Box>

          <Button 
            variant="contained" 
            size="large"
            fullWidth
            onClick={() => navigate('/dashboard')}
          >
            Continue to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return null;
};

// Cancelled Component
export const PaymentCancelled = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const provider = searchParams.get('provider');

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '60vh',
      gap: 3 
    }}>
      <Cancel sx={{ fontSize: 60, color: 'warning.main' }} />
      <Typography variant="h5" color="warning.main">
        Payment Cancelled
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        Your {provider || 'payment'} was cancelled. No charges were made to your account.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/blitzpay')}
        >
          Try Again
        </Button>
      </Box>
    </Box>
  );
};

// Error Component
export const PaymentError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const message = searchParams.get('message') || 'An error occurred during payment processing.';

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '60vh',
      gap: 3 
    }}>
      <Error sx={{ fontSize: 60, color: 'error.main' }} />
      <Typography variant="h5" color="error.main">
        Payment Error
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {message}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
        <Button 
          variant="contained" 
          color="error"
          onClick={() => navigate('/blitzpay')}
        >
          Try Again
        </Button>
      </Box>
    </Box>
  );
};